// src/components/superadmin/SuperAdminDashboard.jsx

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer,
  AreaChart, Area, RadialBarChart, RadialBar, Treemap,
} from "recharts";

import {
  Building2, Users, Package, Activity, TrendingUp,
  Settings, UserPlus, Building, Database,
  BarChart3, Calendar, Clock, Target, BarChart2,
  PieChart as PieChartIcon, IndianRupee, Shield,
} from "lucide-react";

// MUI
import {
  Box, Paper, Typography, Stack, Divider, Skeleton, LinearProgress,
  Tooltip as MuiTooltip, IconButton, Select, MenuItem, FormControl,
  InputLabel, Chip, Alert, Avatar, Button,
} from "@mui/material";

// Service + Auth
import { dashboardService } from "../../services/superAdminDashboardService";
import { useAuth }             from "../../contexts/AuthContext";
import ActivityHeatmap         from "./ActivityHeatmap";

/* ═══════════════════════════════════════════════════════════════════════════
   SMALL PURE COMPONENTS
═══════════════════════════════════════════════════════════════════════════ */

const StatCard = ({ title, value, icon: Icon, color, subtitle, trend, loading }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 2.5,
      borderLeft: `4px solid ${color}`,
      boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      height: "100%",
      "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
      },
    }}
  >
    <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              color: "#6b7280",
              fontWeight: 500,
              mb: 0.5,
              fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.875rem" },
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "#111827",
              lineHeight: 1.2,
              fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
              wordBreak: "break-word",
            }}
          >
            {loading ? "…" : value}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                color: "#9ca3af",
                mt: 0.5,
                display: "block",
                fontSize: { xs: "0.6rem", sm: "0.7rem" },
              }}
            >
              {subtitle}
            </Typography>
          )}
          {trend != null && !loading && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.75 }}>
              <TrendingUp size={14} color="#10B981" />
              <Typography
                variant="caption"
                sx={{ color: "#10B981", fontWeight: 600, fontSize: "0.7rem" }}
              >
                +{trend}% this month
              </Typography>
            </Stack>
          )}
        </Box>
        <Box
          sx={{
            ml: 1.5,
            p: { xs: 1, sm: 1.25 },
            borderRadius: 2,
            bgcolor: `${color}14`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={24} color={color} />
        </Box>
      </Stack>
    </Box>
  </Paper>
);

const QuickActionCard = ({ title, description, icon: Icon, color, onClick }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      p: { xs: 2, sm: 2.5 },
      borderRadius: 2.5,
      boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
      cursor: "pointer",
      transition: "transform 0.2s, box-shadow 0.2s",
      height: "100%",
      "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
      },
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 2,
        bgcolor: `${color}20`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 1.5,
      }}
    >
      <Icon size={22} color={color} />
    </Box>
    <Typography
      variant="subtitle1"
      sx={{ fontWeight: 600, color: "#111827", mb: 0.5, fontSize: { xs: "0.9rem", sm: "1rem" } }}
    >
      {title}
    </Typography>
    <Typography variant="body2" sx={{ color: "#6b7280", fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
      {description}
    </Typography>
  </Paper>
);

// ✅ Replaced the Icon Button group with a clean Select Dropdown
const ChartViewSelector = ({ currentView, onViewChange }) => {
  const options = [
    { key: "bar",     label: "Bar Chart"    },
    { key: "pie",     label: "Pie Chart"    },
    { key: "radial",  label: "Radial Chart" },
    { key: "treemap", label: "Treemap"      },
  ];

  return (
    <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 140 } }}>
      <InputLabel id="chart-type-label" sx={{ fontSize: "0.8rem" }}>Chart Type</InputLabel>
      <Select
        labelId="chart-type-label"
        value={currentView}
        label="Chart Type"
        onChange={(e) => onViewChange(e.target.value)}
        sx={{ fontSize: "0.85rem", borderRadius: 1.5, bgcolor: "#fff" }}
      >
        {options.map((opt) => (
          <MenuItem key={opt.key} value={opt.key} sx={{ fontSize: "0.85rem" }}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

/* ── Skeleton ────────────────────────────────────────────────────────────── */

const DashboardSkeleton = () => (
  <Box sx={{ p: { xs: 2, sm: 3 } }}>
    <Skeleton variant="text" width={280} height={40} />
    <Skeleton variant="text" width={360} height={24} sx={{ mb: 3 }} />
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
        gap: 2,
        mb: 3,
      }}
    >
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} variant="rounded" height={120} sx={{ borderRadius: 2.5 }} />
      ))}
    </Box>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 2,
        mb: 3,
      }}
    >
      {Array.from({ length: 2 }).map((_, i) => (
        <Skeleton key={i} variant="rounded" height={400} sx={{ borderRadius: 2.5 }} />
      ))}
    </Box>
    <Skeleton variant="rounded" height={300} sx={{ borderRadius: 2.5 }} />
  </Box>
);

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════════════ */

// Color palette for companies
const COMPANY_COLORS = [
  "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6",
  "#EC4899", "#14B8A6", "#F97316", "#6366F1", "#84CC16",
  "#06B6D4", "#D946EF", "#F43F5E", "#22C55E", "#EAB308",
];

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();

  /* ── Raw data ── */
  const [allCompanies, setAllCompanies] = useState([]);
  const [allUsers,     setAllUsers]     = useState([]);
  const [allAssets,    setAllAssets]    = useState([]);
  const [auditLogs,    setAuditLogs]    = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  /* ── Loaded flags ── */
  const [loaded, setLoaded] = useState({
    companies: false, users: false, assets: false, logs: false,
  });

  /* ── Derived state ── */
  const [metrics, setMetrics] = useState({
    totalCompanies: 0, totalUsers: 0, totalAssets: 0,
    totalValue: 0, activeAlerts: 0, systemHealth: "Excellent", monthlyGrowth: 0,
  });
  const [growthTrend, setGrowthTrend] = useState([]);

  /* ── UI state ── */
  const [currentDateTime,     setCurrentDateTime]     = useState(new Date());
  const [companyChartView,    setCompanyChartView]    = useState("bar");
  
  /* ── Company Chart Filters ── */
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState("all");
  const [selectedCompany,       setSelectedCompany]       = useState(null);
  
  const [selectedGrowthCompanyId, setSelectedGrowthCompanyId] = useState("all");

  /* Clock */
  useEffect(() => {
    const t = setInterval(() => setCurrentDateTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  /* ── Subscriptions ── */
  useEffect(() => {
    if (authLoading || !currentUser) return;

    const markLoaded = (k) => setLoaded((p) => ({ ...p, [k]: true }));

    const unsub1 = dashboardService.subscribeToCompanies((l) => { setAllCompanies(l); markLoaded("companies"); });
    const unsub2 = dashboardService.subscribeToUsers((l)     => { setAllUsers(l);     markLoaded("users");     });
    const unsub3 = dashboardService.subscribeToAssets((l)    => { setAllAssets(l);    markLoaded("assets");    });
    const unsub4 = dashboardService.subscribeToAuditLogs((l) => { setAuditLogs(l);    markLoaded("logs");      });
    const unsub5 = dashboardService.subscribeToAccessRequests((l) => { setAccessRequests(l); });
    const cleanup = (r) => {
      if (!r) return;
      if (typeof r === "function") return r;
      if (r instanceof Promise) {
        let fn;
        r.then((f) => { fn = f; }).catch(console.error);
        return () => { if (typeof fn === "function") fn(); };
      }
    };

    const cs = [cleanup(unsub1), cleanup(unsub2), cleanup(unsub3), cleanup(unsub4), cleanup(unsub5)];
    return () => cs.forEach((fn) => { if (typeof fn === "function") fn(); });
  }, [currentUser, authLoading]);

  /* ── Scoped arrays for the growth trend chart ── */
  const scopedGrowthData = useMemo(() => {
    if (selectedGrowthCompanyId === "all") {
      return { companies: allCompanies, users: allUsers, assets: allAssets };
    }

    const cId = selectedGrowthCompanyId;

    const scopedCompanies = allCompanies.filter(
      (c) => (c.companyId || c.id) === cId,
    );
    const scopedUsers = allUsers.filter((u) => u.companyId === cId);
    const scopedAssets = allAssets.filter((a) => a.companyId === cId);

    return { companies: scopedCompanies, users: scopedUsers, assets: scopedAssets };
  }, [selectedGrowthCompanyId, allCompanies, allUsers, allAssets]);

  /* ── Derived data (metrics + trend) ── */
  useEffect(() => {
    if (!loaded.companies || !loaded.users || !loaded.assets || !loaded.logs) return;

    setMetrics(dashboardService.buildDashboardMetrics(allCompanies, allUsers, allAssets));

    setGrowthTrend(
      dashboardService.buildGrowthTrend(
        scopedGrowthData.companies,
        scopedGrowthData.users,
        scopedGrowthData.assets,
      ),
    );
  }, [
    loaded,
    allCompanies, allUsers, allAssets,
    scopedGrowthData,
    auditLogs,
  ]);

  /* ── Company-wise distribution data (Users per company) ── */
  const companyDistribution = useMemo(() => {
    if (!loaded.companies || !loaded.users || !loaded.assets) return [];

    const rows = allCompanies.map((company, idx) => {
      const cId = company.companyId || company.id;

      const companyUsers = allUsers.filter(
        (u) => u.companyId === cId || u.companyId === company.id,
      );
      const companyAssets = allAssets.filter(
        (a) => a.companyId === cId || a.companyId === company.id,
      );
      const totalValue = companyAssets.reduce(
        (sum, a) => sum + (parseFloat(a.purchaseValue || a.cost) || 0),
        0,
      );

      const name = company.name || "Unknown";

      return {
        companyId:   cId,
        company:     name.length > 15 ? name.slice(0, 15) + "…" : name,
        fullName:    name,
        users:       companyUsers.length,
        assets:      companyAssets.length,
        value:       totalValue,
        color:       COMPANY_COLORS[idx % COMPANY_COLORS.length],
      };
    });

    return rows.sort((a, b) => (b.users || 0) - (a.users || 0));
  }, [allCompanies, allUsers, allAssets, loaded]);

  /* ── Filtered Distribution based on Company Filter ── */
  const filteredCompanyDistribution = useMemo(() => {
    if (selectedCompanyFilter === "all") return companyDistribution;
    return companyDistribution.filter((d) => d.companyId === selectedCompanyFilter);
  }, [companyDistribution, selectedCompanyFilter]);

  /* Clear company selection when filter changes */
  useEffect(() => {
    setSelectedCompany(null);
  }, [selectedCompanyFilter]);

  const isLoading =
    authLoading ||
    !loaded.companies || !loaded.users || !loaded.assets || !loaded.logs;

  /* ── Company filter options (used for growth trend) ── */
  const companyOptions = useMemo(
    () => [
      { id: "all", name: "All Companies" },
      ...allCompanies
        .map((c) => ({ id: c.companyId || c.id, name: c.name || "Unknown Company" }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    ],
    [allCompanies],
  );

  /* ── Navigation ── */
  const nav = {
    createCompany: () => navigate("/super-admin/masters/companies"),
    manageUsers:   () => navigate("/super-admin/users"),
    analytics:     () => navigate("/super-admin/reports"),
    settings:      () => navigate("/super-admin/settings"),
  };

  /* ── Pending Requests Calculations ── */
  const pendingAdminRequests = useMemo(() => {
    return accessRequests.filter(
      (r) => r.status?.toLowerCase() === "pending" &&
             (r.role?.toLowerCase() === "admin" || r.requestType?.toLowerCase() === "admin")
    ).length;
  }, [accessRequests]);

  const pendingUserRequests = useMemo(() => {
    return accessRequests.filter(
      (r) => r.status?.toLowerCase() === "pending" &&
             (r.role?.toLowerCase() === "user" || r.requestType?.toLowerCase() === "user" || !r.role)
    ).length;
  }, [accessRequests]);

  /* ── Chart helpers ── */
  const formatMetricValue = (val) => val?.toLocaleString?.() ?? val;

  const EnhancedTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <Paper elevation={4} sx={{ p: 1.5, borderRadius: 2, minWidth: 180 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: d.color }} />
          <Typography variant="subtitle2" fontWeight={700}>{d.fullName || d.company}</Typography>
        </Stack>
        {[
          ["Users",       d.users?.toLocaleString()],
          ["Assets",      d.assets?.toLocaleString()],
          ["Asset Value", dashboardService.formatCurrency(d.value || 0)],
        ].map(([l, v]) => (
          <Stack key={l} direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="caption" color="text.secondary">{l}:</Typography>
            <Typography variant="caption" fontWeight={700}>{v}</Typography>
          </Stack>
        ))}
      </Paper>
    );
  };

  const handleChartClick = (data) => {
    const cId = data?.activePayload?.[0]?.payload?.companyId;
    if (cId) setSelectedCompany((prev) => (prev === cId ? null : cId));
  };

  // Use filtered data for the chart
  const chartData = selectedCompany
    ? filteredCompanyDistribution.filter((d) => d.companyId === selectedCompany)
    : filteredCompanyDistribution;

  const cellStyle = (entry) => ({
    filter: selectedCompany && selectedCompany !== entry.companyId ? "opacity(0.3)" : "none",
    cursor: "pointer",
  });

  const renderCompanyChart = () => {
    if (filteredCompanyDistribution.length === 0) {
      return (
        <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="body2" color="text.secondary">No company data available</Typography>
        </Box>
      );
    }

    switch (companyChartView) {
      case "pie":
        return (
          <PieChart onClick={handleChartClick}>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={false}
              outerRadius="80%"
              dataKey="users"
              isAnimationActive={false}
            >
              {chartData.map((entry, i) => (
                <Cell
                  key={i}
                  fill={entry.color}
                  stroke={selectedCompany === entry.companyId ? "#2563eb" : "#fff"}
                  strokeWidth={selectedCompany === entry.companyId ? 3 : 1}
                  style={cellStyle(entry)}
                />
              ))}
            </Pie>
            <Tooltip content={<EnhancedTooltip />} />
          </PieChart>
        );

      case "bar":
        return (
          <BarChart
            data={chartData}
            onClick={handleChartClick}
            margin={{ top: 20, right: 30, left: 0, bottom: 40 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e4e7" vertical={false} />
            <XAxis
              dataKey="company"
              angle={-45}
              textAnchor="end"
              height={60}
              interval={0}
              tick={{ fontSize: 11, fill: "#6b7280" }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#6b7280" }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<EnhancedTooltip />} cursor={{ fill: "rgba(59,130,246,0.08)" }} />
            <Bar
              dataKey="users"
              isAnimationActive={false}
              cursor="pointer"
              radius={[6, 6, 0, 0]}
              maxBarSize={60}
            >
              {chartData.map((entry, i) => (
                <Cell
                  key={i}
                  fill={entry.color}
                  stroke={selectedCompany === entry.companyId ? "#2563eb" : "none"}
                  strokeWidth={selectedCompany === entry.companyId ? 2 : 0}
                  style={cellStyle(entry)}
                />
              ))}
            </Bar>
          </BarChart>
        );

      case "radial":
        return (
          <RadialBarChart
            cx="50%"
            cy="50%"
            innerRadius="30%"
            outerRadius="90%"
            barSize={40}
            data={chartData.map((d) => ({ ...d, fill: d.color, uv: d.users }))}
            onClick={handleChartClick}
          >
            <RadialBar
              minAngle={15}
              background
              clockWise
              dataKey="uv"
              isAnimationActive={false}
              cursor="pointer"
              cornerRadius={10}
            />
            <Tooltip content={<EnhancedTooltip />} />
          </RadialBarChart>
        );

      case "treemap": {
        const TmContent = (props) => {
          const { x, y, width, height, index } = props;
          const d = chartData[index];
          if (!d) return null;
          const show = width > 80 && height > 60;
          return (
            <g>
              <rect
                x={x}
                y={y}
                width={width}
                height={height}
                style={{
                  fill: d.color,
                  stroke: selectedCompany === d.companyId ? "#2563eb" : "#fff",
                  strokeWidth: selectedCompany === d.companyId ? 4 : 2,
                  cursor: "pointer",
                  filter: selectedCompany && selectedCompany !== d.companyId ? "opacity(0.4)" : "none",
                }}
              />
              {show && (
                <>
                  <text
                    x={x + width / 2}
                    y={y + height / 2 - 5}
                    textAnchor="middle"
                    fill="#fff"
                    fontSize={14}
                    fontWeight="bold"
                  >
                    {d.company}
                  </text>
                  <text
                    x={x + width / 2}
                    y={y + height / 2 + 15}
                    textAnchor="middle"
                    fill="#fff"
                    fontSize={12}
                  >
                    {formatMetricValue(d.users)} users
                  </text>
                </>
              )}
            </g>
          );
        };
        return (
          <Treemap
            data={chartData}
            dataKey="users"
            stroke="#fff"
            strokeWidth={2}
            content={<TmContent />}
            isAnimationActive={false}
            onClick={handleChartClick}
          >
            <Tooltip content={<EnhancedTooltip />} />
          </Treemap>
        );
      }

      default:
        return null;
    }
  };

  /* ── Guards ── */
  if (authLoading || isLoading) return <DashboardSkeleton />;

  if (!currentUser) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
        <Alert severity="warning">Please log in to view the super admin dashboard.</Alert>
      </Box>
    );
  }

  /* ═════════════════════════════════════════════════════════════════════════
     RENDER
  ═════════════════════════════════════════════════════════════════════════ */

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100vw",
        minHeight: "100vh",
        bgcolor: "#f5f7fa",
        p: { xs: 1.5, sm: 2, md: 2.5 },
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      <Box sx={{ maxWidth: "1800px", mx: "auto" }}>

        {/* ── HEADER ──────────────────────────────────────────────────── */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={{ xs: 2, sm: 2.5 }}
          sx={{ mb: { xs: 2.5, sm: 3.5 } }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700, color: "#1f2937", mb: 0.5,
                fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2rem" },
                wordBreak: "break-word",
              }}
            >
              Super Admin Dashboard
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#6b7280", fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" } }}
            >
              Complete Asset Management System Overview
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 1.5, borderRadius: 2.5, boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
              width: { xs: "100%", sm: "auto" }, flexShrink: 0,
            }}
          >
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Calendar size={18} color="#4b5563" />
                <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151", fontSize: { xs: "0.7rem", sm: "0.875rem" } }}>
                  {currentDateTime.toLocaleDateString("en-US", {
                    weekday: "long", month: "long", day: "numeric", year: "numeric",
                  })}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Clock size={18} color="#4b5563" />
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827", fontSize: { xs: "0.7rem", sm: "0.875rem" } }}>
                  {currentDateTime.toLocaleTimeString("en-US", {
                    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
                  })}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Stack>

        {/* ── KPI STAT CARDS ──────────────────────────────────────────── */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
            gap: { xs: 1.5, sm: 2, md: 2.5 },
            mb: { xs: 2.5, sm: 3.5 },
          }}
        >
          <StatCard title="Total Companies" value={metrics.totalCompanies}
            icon={Building2} color="#3B82F6" subtitle="Active organizations" trend={8.5} />
          <StatCard title="Total Users" value={metrics.totalUsers}
            icon={Users} color="#10B981" subtitle="Across all companies" trend={12.3} />
          <StatCard title="Total Assets" value={metrics.totalAssets.toLocaleString()}
            icon={Package} color="#F59E0B" subtitle="System-wide assets" trend={metrics.monthlyGrowth} />
          <StatCard
            title="Portfolio Value"
            value={dashboardService.formatCurrency(metrics.totalValue)}
            icon={IndianRupee} color="#EF4444" subtitle="Total asset value" trend={18.7}
          />
        </Box>

        {/* ── QUICK ACTIONS ───────────────────────────────────────────── */}
        <Box sx={{ mb: { xs: 2.5, sm: 3.5 } }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1f2937", mb: 2, fontSize: { xs: "1rem", sm: "1.15rem" } }}>
            Quick Actions
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
              gap: { xs: 1.5, sm: 2 },
            }}
          >
            {[
              { title: "Create Company", desc: "Add new company to the system", icon: Building, color: "#3B82F6", fn: nav.createCompany },
              { title: "Manage Users",   desc: "Add, edit, or remove users",   icon: Users,    color: "#10B981", fn: nav.manageUsers   },
              { title: "Reports",        desc: "View performance analytics",   icon: BarChart3, color: "#F59E0B", fn: nav.analytics     },
              { title: "Settings",       desc: "Configure system settings",    icon: Settings,  color: "#8B5CF6", fn: nav.settings      },
              {
                title: `Pending Admin Requests (${pendingAdminRequests})`,
                desc: pendingAdminRequests > 0 ? "Action required: Review access" : "No pending requests",
                icon: Shield,
                color: pendingAdminRequests > 0 ? "#EF4444" : "#9CA3AF",
                fn: () => navigate("/super-admin/access-requests")
              },
              {
                title: `Pending User Requests (${pendingUserRequests})`,
                desc: pendingUserRequests > 0 ? "Action required: Review access" : "No pending requests",
                icon: UserPlus,
                color: pendingUserRequests > 0 ? "#F59E0B" : "#9CA3AF",
                fn: () => navigate("/super-admin/access-requests")
              },
            ].map(({ title, desc, icon, color, fn }) => (
              <QuickActionCard key={title} title={title} description={desc} icon={icon} color={color} onClick={fn} />
            ))}
          </Box>
        </Box>
        {/* ── CHARTS GRID ─────────────────────────────────────────────── */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
            gap: { xs: 1.5, sm: 2, md: 3 },
            mb: { xs: 2.5, sm: 3.5 },
            minWidth: 0,
          }}
        >
          {/* Company-wise Distribution */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2, md: 2.5 },
              borderRadius: 2.5,
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={{ xs: 1.5, sm: 2 }}
              sx={{ mb: 2 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="h6" sx={{ fontWeight: 600, color: "#1f2937", fontSize: { xs: "0.95rem", sm: "1.05rem" } }}>
                    Company-wise Users
                  </Typography>
                  {selectedCompany && (
                    <Chip
                      size="small"
                      label={`Filtered: ${filteredCompanyDistribution.find(c => c.companyId === selectedCompany)?.fullName || ""}`}
                      onDelete={() => setSelectedCompany(null)}
                      sx={{ bgcolor: "#EFF6FF", color: "#2563eb", fontWeight: 600 }}
                    />
                  )}
                </Stack>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                
                {/* ── COMPANY FILTER ── */}
                <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
                  <InputLabel id="company-filter-label" sx={{ fontSize: "0.8rem" }}>Company</InputLabel>
                  <Select
                    labelId="company-filter-label"
                    value={selectedCompanyFilter}
                    label="Company"
                    onChange={(e) => setSelectedCompanyFilter(e.target.value)}
                    sx={{ fontSize: "0.85rem", borderRadius: 1.5, bgcolor: "#fff" }}
                  >
                    <MenuItem value="all" sx={{ fontSize: "0.85rem", fontStyle: "italic" }}>
                      All Companies
                    </MenuItem>
                    {companyDistribution.map((c) => (
                      <MenuItem key={c.companyId} value={c.companyId} sx={{ fontSize: "0.85rem" }}>
                        {c.fullName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* ── CHART TYPE FILTER ── */}
                <ChartViewSelector currentView={companyChartView} onViewChange={setCompanyChartView} />
              </Stack>
            </Stack>

            {/* Chart container */}
            <Box sx={{ width: "100%", height: { xs: 320, sm: 360, md: 400 } }}>
              <ResponsiveContainer width="100%" height="100%">
                {renderCompanyChart()}
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Activity Heatmap */}
          <Paper
            elevation={0}
            sx={{ 
              p: { xs: 1.5, sm: 2, md: 2.5 }, 
              borderRadius: 2.5, 
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
              minWidth: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1f2937", mb: 2, fontSize: { xs: "0.95rem", sm: "1.05rem" } }}>
              Activity Heatmap
            </Typography>
            <ActivityHeatmap className="compact" />
          </Paper>
        </Box>
        {/* ── GROWTH TREND ────────────────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{ p: { xs: 1.5, sm: 2, md: 2.5 }, borderRadius: 2.5, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={{ xs: 1.5, sm: 2 }}
            sx={{ mb: 2 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#1f2937", fontSize: { xs: "0.95rem", sm: "1.05rem" } }}
              >
                System Growth Trend
              </Typography>
              {selectedGrowthCompanyId !== "all" && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25, fontSize: "0.7rem" }}>
                  Showing growth for{" "}
                  <strong>
                    {companyOptions.find((c) => c.id === selectedGrowthCompanyId)?.name ?? "selected company"}
                  </strong>
                </Typography>
              )}
            </Box>

            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 200 }, alignSelf: { xs: "stretch", sm: "center" } }}>
              <InputLabel id="growth-company-filter-label" sx={{ fontSize: "0.8rem" }}>Company</InputLabel>
              <Select
                labelId="growth-company-filter-label"
                value={selectedGrowthCompanyId}
                label="Company"
                onChange={(e) => setSelectedGrowthCompanyId(e.target.value)}
                sx={{ fontSize: "0.85rem", borderRadius: 1.5, bgcolor: "#fff" }}
              >
                {companyOptions.map((c) => (
                  <MenuItem key={c.id} value={c.id} sx={{ fontSize: "0.85rem" }}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Summary metrics */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
              gap: 1.5,
              mb: 2.5,
            }}
          >
            {[
              {
                label: "Total Companies",
                value: scopedGrowthData.companies.length,
                added: dashboardService.countAddedThisMonth(scopedGrowthData.companies),
              },
              {
                label: "Total Users",
                value: scopedGrowthData.users.length,
                added: dashboardService.countAddedThisMonth(scopedGrowthData.users),
              },
              {
                label: "Total Assets",
                value: scopedGrowthData.assets.length,
                added: dashboardService.countAddedThisMonth(scopedGrowthData.assets, true),
              },
            ].map(({ label, value, added }) => (
              <Paper
                key={label}
                elevation={0}
                sx={{ p: 1.5, borderRadius: 2, bgcolor: "#F8FAFC", border: "1px solid #e5e7eb" }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.7rem" }}>
                  {label}
                </Typography>
                <Typography variant="h6" fontWeight={700} sx={{ color: "#111827", my: 0.25 }}>
                  {value}
                </Typography>
                <Typography variant="caption" sx={{ color: "#10B981", fontWeight: 600, fontSize: "0.7rem" }}>
                  +{added} this month
                </Typography>
              </Paper>
            ))}
          </Box>

          {/* Area chart */}
          {growthTrend.length > 0 ? (
            <Box sx={{ width: "100%", height: { xs: 300, sm: 350 } }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={growthTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} axisLine={{ stroke: "#e0e0e0" }} />
                  <YAxis tick={{ fontSize: 12 }} axisLine={{ stroke: "#e0e0e0" }} />
                  <Tooltip
                    formatter={(value, name, props) => {
                      const mg = props.payload?.monthlyGrowth;
                      const colorMap = { companies: "#8B5CF6", users: "#10B981", assets: "#F59E0B" };
                      return [
                        <div key={name} style={{ margin: "4px 0" }}>
                          <div style={{ fontWeight: "bold", color: colorMap[name] }}>
                            {name.charAt(0).toUpperCase() + name.slice(1)}: {value.toLocaleString()}
                          </div>
                          {mg && (
                            <div style={{ fontSize: 12, color: "#666" }}>
                              +{mg[name]} added this month
                            </div>
                          )}
                        </div>,
                      ];
                    }}
                    labelFormatter={(l) => `Month: ${l}`}
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                      padding: 12,
                    }}
                    cursor={{ fill: "rgba(59,130,246,0.1)" }}
                  />
                  <Legend wrapperStyle={{ paddingTop: 20 }} iconType="rect" />
                  {[
                    { key: "companies", color: "#8B5CF6", label: "Companies" },
                    { key: "users",     color: "#10B981", label: "Users"     },
                    { key: "assets",    color: "#F59E0B", label: "Assets"    },
                  ].map(({ key, color, label }) => (
                    <Area key={key} type="monotone" dataKey={key} stackId={key}
                      stroke={color} fill={color} fillOpacity={0.8} strokeWidth={2} name={label} />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Skeleton variant="rounded" height={350} sx={{ borderRadius: 2 }} />
          )}

          {/* Growth rate badges */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
            {["companies", "users", "assets"].map((key) => {
              const prevAdded = growthTrend[growthTrend.length - 2]?.monthlyGrowth?.[key] || 0;
              const currAdded = growthTrend[growthTrend.length - 1]?.monthlyGrowth?.[key] || 0;

              const isNoActivity = prevAdded === 0 && currAdded === 0;
              const pct = dashboardService.calcGrowthPercentage(prevAdded, currAdded);

              return (
                <Paper
                  key={key}
                  elevation={0}
                  sx={{ flex: 1, p: 1.5, borderRadius: 2, bgcolor: "#F8FAFC", border: "1px solid #e5e7eb" }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.7rem" }}>
                    {key.charAt(0).toUpperCase() + key.slice(1)} Growth
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: isNoActivity ? "#9ca3af" : "#2563eb" }}>
                    {isNoActivity ? "N/A" : `${pct}%`}
                  </Typography>
                </Paper>
              );
            })}
          </Stack>
        </Paper>

      </Box>
    </Box>
  );
};

export default SuperAdminDashboard;